import { useCallback, useEffect, useRef, useState } from 'react'

/** Parameters sent with every paginated request. */
export type CursorPaginationRequest = {
    pageSize: number
    cursor?: string
}

/** Metadata expected from the API, alongside any endpoint-specific fields. */
export type CursorPaginationResponse = {
    total: number
    /** Cursor for the next page; empty when there are no more results. */
    cursor?: string | null
}

/** Data source, filters, and presentation defaults for cursor pagination. */
export type UseCursorPaginationOptions<Filters, Response extends CursorPaginationResponse> = {
    fetchPage: (request: Filters & CursorPaginationRequest) => Promise<Response>
    filters: Filters
    pageSize?: number
    /** Defaults to true. Set to false to defer requests until filters are ready; enabling fetches page one. */
    enabled?: boolean
    errorMessage?: string
}

/** Request state and props that can be passed directly to Pagination. */
export type CursorPaginationResult<Response> = {
    response: Response | null
    error: string | null
    loading: boolean
    paginationProps: {
        page: number
        pageSize: number
        totalCount: number
        onPageChange: (page: number) => void
        loading: boolean
    }
}

/**
 * Fetches cursor-based pages and remembers cursors for backward navigation.
 * Keep fetchPage and filters references stable between renders (useCallback/useMemo
 * for inline values). Changing either, or pageSize, resets pagination to page one.
 */
export function useCursorPagination<Filters, Response extends CursorPaginationResponse> ({
    fetchPage,
    filters,
    pageSize = 25,
    enabled = true,
    errorMessage = 'Something went wrong while loading your data'
}: UseCursorPaginationOptions<Filters, Response>): CursorPaginationResult<Response> {
    const [response, setResponse] = useState<Response | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState(false)
    const [page, setPage] = useState(1)
    const [cursors, setCursors] = useState<Array<string | null | undefined>>([undefined])
    const requestId = useRef(0)

    const loadPage = useCallback(async (requestedPage = 1, cursor?: string) => {
        if (!enabled) return
        const currentRequestId = ++requestId.current
        setError(null)
        setLoading(true)
        try {
            const result = await fetchPage({ ...filters, pageSize, cursor })
            if (currentRequestId !== requestId.current) return
            setResponse(result)
            setPage(requestedPage)
            setCursors((current) => [...current.slice(0, requestedPage), result.cursor])
        } catch (err) {
            if (currentRequestId !== requestId.current) return
            setError(err instanceof Error && err.message ? err.message : errorMessage)
        } finally {
            if (currentRequestId === requestId.current) setLoading(false)
        }
    }, [fetchPage, filters, pageSize, errorMessage, enabled])

    useEffect(() => {
        setPage(1)
        setCursors([undefined])
        setResponse(null)
        setError(null)
        setLoading(false)
        loadPage()
        return () => { requestId.current += 1 }
    }, [loadPage])

    const onPageChange = (requestedPage: number) => {
        const cursor = cursors[requestedPage - 1]
        if (loading || requestedPage === page || !Number.isInteger(requestedPage) || requestedPage < 1 ||
            (requestedPage > 1 && !cursor)) return
        loadPage(requestedPage, cursor || undefined)
    }

    return {
        response,
        error,
        loading,
        paginationProps: {
            page,
            pageSize,
            totalCount: response?.total || 0,
            onPageChange,
            loading
        }
    }
}
